import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Paperclip, Loader2, X, FileText, Download, Pin, Reply, Smile, Cloud, SmilePlus, Zap, ChevronDown, Mic, Forward, Copy, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkUpload";
import { useAuth } from "@/context/AuthContext";
import { resolveMediaUrl, thumbUrl } from "@/components/FileUpload";
import { UserAvatar } from "@/components/UserAvatar";
import { SharePointPickerDialog } from "@/components/SharePointPickerDialog";
import { VoiceRecorderButton } from "@/components/VoiceRecorderButton";
import { ForwardDialog } from "@/components/ForwardDialog";
import { STATUS_LABEL, TYPE_LABEL } from "@/lib/constants";
import { downloadMediaFile } from "@/lib/mediaDownload";

const POLL_MS = 8000;
const MAX_MB = 500;

const EmojiPicker = lazy(() => import("emoji-picker-react"));

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "🙏", "🔥"];

function ReactionChips({ m, uid, dark, onToggle }) {
  const entries = Object.entries(m.reactions || {}).filter(([, users]) => users.length > 0);
  if (entries.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 mt-1 ${m.user_id === uid ? "justify-end" : ""}`} data-testid={`chat-reactions-${m.id}`}>
      {entries.map(([emoji, users]) => {
        const own = users.some((u) => u.user_id === uid);
        return (
          <button
            key={emoji}
            data-testid={`chat-reaction-${m.id}-${emoji}`}
            onClick={() => onToggle(m, emoji)}
            title={users.map((u) => u.user_name).join(", ")}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[12px] border transition ${
              own
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : dark
                  ? "bg-neutral-800 border-neutral-700 text-neutral-300 hover:border-neutral-500"
                  : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400"
            }`}
          >
            <span>{emoji}</span>
            {users.length > 1 && <span className="font-semibold text-[10px]">{users.length}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ReplyQuote({ r, mine, dark, onJump }) {
  if (!r) return null;
  return (
    <button
      onClick={onJump}
      data-testid="chat-reply-quote"
      className={`w-full text-left mb-1 rounded-lg px-2.5 py-1.5 border-l-4 text-xs ${
        mine ? "bg-white/15 border-white/60" : dark ? "bg-black/25 border-blue-400" : "bg-black/5 border-blue-500"
      }`}
    >
      <span className={`block font-bold ${mine ? "text-white/90" : "text-blue-600"}`}>{r.user_name}</span>
      <span className={`block truncate ${mine ? "text-white/75" : dark ? "text-neutral-300" : "text-neutral-600"}`}>
        {r.text || "📎 Anhang"}
      </span>
    </button>
  );
}

function SystemActivityGroup({ items, dark, onOpenRef }) {
  const [open, setOpen] = useState(false);
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const line = (m) => (
    <button
      key={m.id}
      data-testid={`chat-activity-${m.id}`}
      onClick={() => m.content_ref && onOpenRef(m.content_ref)}
      className={`w-full flex items-start gap-2 text-left px-2.5 py-1 rounded-lg transition ${
        m.content_ref ? (dark ? "hover:bg-neutral-800 cursor-pointer" : "hover:bg-neutral-100 cursor-pointer") : "cursor-default"
      }`}
    >
      <span className={`text-[10px] tabular-nums shrink-0 mt-[1px] ${dark ? "text-neutral-600" : "text-neutral-300"}`}>{fmtTime(m.created_at)}</span>
      <span className={`text-[11px] leading-snug line-clamp-2 ${dark ? "text-neutral-500" : "text-neutral-400"}`}>
        {m.text}{m.content_ref ? " →" : ""}
      </span>
    </button>
  );
  if (items.length === 1) return <div className="py-0.5" data-testid={`chat-activity-single-${items[0].id}`}>{line(items[0])}</div>;
  return (
    <div className="py-0.5" data-testid="chat-activity-group">
      <button
        data-testid="chat-activity-toggle"
        onClick={() => setOpen((v) => !v)}
        className={`mx-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
          dark
            ? "bg-neutral-800/70 border-neutral-700 text-neutral-400 hover:text-neutral-200"
            : "bg-neutral-50 border-neutral-200 text-neutral-400 hover:text-neutral-600"
        }`}
      >
        <Zap className="h-3 w-3" /> {items.length} Aktivitäten
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-1 space-y-0">{items.map(line)}</div>}
    </div>
  );
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Heute";
  if (d.toDateString() === yesterday.toDateString()) return "Gestern";
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

function renderText(text, memberNames, dark) {
  if (!text) return null;
  if (!memberNames.length) return text;
  const escaped = memberNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(@(?:${escaped.join("|")}))`, "g"));
  return parts.map((p, i) =>
    p.startsWith("@") && memberNames.includes(p.slice(1)) ? (
      <span key={i} className={`font-bold rounded px-0.5 ${dark ? "text-blue-300 bg-blue-500/15" : "text-blue-700 bg-blue-100"}`}>{p}</span>
    ) : (
      p
    ),
  );
}

function ContentRefCard({ cref, dark, onOpen }) {
  if (!cref) return null;
  return (
    <div
      data-testid={`chat-content-ref-${cref.id}`}
      className={`mt-1.5 rounded-xl border overflow-hidden ${dark ? "bg-neutral-900 border-neutral-700" : "bg-white border-neutral-200"}`}
    >
      {cref.video_url ? (
        <video src={resolveMediaUrl(cref.video_url)} controls playsInline preload="metadata" className="w-full h-52 bg-black object-contain" />
      ) : cref.media_url ? (
        <img src={thumbUrl(cref.media_url, 480)} alt="" loading="lazy" decoding="async" className="w-full h-40 object-cover bg-neutral-200" />
      ) : null}
      <div className="px-3 py-2.5">
        <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${dark ? "text-neutral-500" : "text-neutral-400"}`}>
          <Pin className="h-3 w-3" /> Pipeline-Beitrag
        </div>
        <div className={`text-sm font-semibold leading-snug mt-0.5 ${dark ? "text-white" : "text-neutral-900"}`}>{cref.title}</div>
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <span className={`text-[11px] ${dark ? "text-neutral-400" : "text-neutral-500"}`}>
            {STATUS_LABEL[cref.status] || cref.status}{cref.content_type ? ` · ${TYPE_LABEL[cref.content_type] || cref.content_type}` : ""}
          </span>
          <button
            data-testid={`chat-content-open-${cref.id}`}
            onClick={onOpen}
            className="text-[11px] font-bold text-blue-500 hover:text-blue-400"
          >
            In Pipeline öffnen →
          </button>
        </div>
      </div>
    </div>
  );
}

function Attachment({ att, dark, transcript }) {
  const [lightbox, setLightbox] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  if (!att) return null;
  const url = resolveMediaUrl(att.url);
  const isPdf = /\.pdf$/i.test(att.filename || "");
  const doDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    await downloadMediaFile({ url: att.url, title: att.filename });
    setBusy(false);
  };
  const openDocPreview = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loadingPreview) return;
    setLoadingPreview(true);
    try {
      const { data } = await api.get(att.url.replace(/^\/api/, ""), { responseType: "blob", timeout: 120000 });
      const blob = data.type === "application/pdf" ? data : new Blob([data], { type: "application/pdf" });
      setDocPreview(URL.createObjectURL(blob));
    } catch {
      toast.error("Vorschau konnte nicht geladen werden");
    }
    setLoadingPreview(false);
  };
  const closeDocPreview = () => {
    if (docPreview) URL.revokeObjectURL(docPreview);
    setDocPreview(null);
  };
  if (att.kind === "audio")
    return (
      <div className="mt-1.5 min-w-[220px]" data-testid="chat-attachment-audio">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold opacity-80 mb-1">
          <Mic className="h-3 w-3" /> Sprachnachricht
          {att.duration ? ` · ${Math.floor(att.duration / 60)}:${String(att.duration % 60).padStart(2, "0")}` : ""}
        </div>
        <audio src={url} controls preload="metadata" className="w-full max-w-[260px] h-10" />
        {transcript && (
          <p className="mt-1 text-[12px] italic leading-snug opacity-85 max-w-[260px]" data-testid="chat-voice-transcript">
            📝 {transcript}
          </p>
        )}
      </div>
    );
  if (att.kind === "image")
    return (
      <div className="relative mt-1.5 group/att">
        <button type="button" onClick={() => setLightbox(true)} className="block" data-testid="chat-attachment-image-open">
          <img src={thumbUrl(att.url, 480)} alt={att.filename} loading="lazy" decoding="async" className="h-48 w-64 max-w-full rounded-lg border border-black/10 object-cover bg-neutral-200" />
        </button>
        <button
          type="button"
          onClick={doDownload}
          data-testid="chat-attachment-download"
          className="absolute bottom-1.5 right-1.5 h-7 w-7 rounded-full bg-black/55 text-white flex items-center justify-center opacity-80 hover:opacity-100"
          title="Herunterladen"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        </button>
        {lightbox && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(false)}
            data-testid="chat-image-lightbox"
          >
            <img src={url} alt={att.filename} className="max-h-[90vh] max-w-full rounded-lg object-contain" />
            <button
              type="button"
              onClick={doDownload}
              className="absolute bottom-6 right-6 h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur"
              title="Herunterladen"
              data-testid="chat-lightbox-download"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
            </button>
            <button
              type="button"
              className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur"
              onClick={() => setLightbox(false)}
              data-testid="chat-lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  if (att.kind === "video")
    return (
      <div className="mt-1.5">
        <video src={url} controls playsInline preload="metadata" className="h-52 w-72 max-w-full rounded-lg bg-black object-contain" />
        <button
          type="button"
          onClick={doDownload}
          data-testid="chat-attachment-download"
          className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${dark ? "text-neutral-300" : "text-neutral-500"} hover:underline`}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} {att.filename || "Video herunterladen"}
        </button>
      </div>
    );
  return (
    <div className="mt-1.5">
      <div
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium border ${
          dark ? "bg-neutral-800 border-neutral-700 text-neutral-200" : "bg-white border-neutral-200 text-neutral-700"
        }`}
        data-testid="chat-attachment-file"
      >
        <FileText className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1">{att.filename}</span>
        {isPdf && (
          <button
            type="button"
            onClick={openDocPreview}
            data-testid="chat-attachment-preview"
            className="shrink-0 inline-flex items-center gap-1 font-bold text-blue-500 hover:text-blue-400"
            title="Ansehen"
          >
            {loadingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Ansehen"}
          </button>
        )}
        <button type="button" onClick={doDownload} data-testid="chat-attachment-download" title="Herunterladen" className="shrink-0">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 opacity-60 hover:opacity-100" />}
        </button>
      </div>
      {docPreview && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col p-3 sm:p-6" data-testid="chat-doc-preview">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-white text-sm font-medium truncate">{att.filename}</span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={doDownload}
                className="h-9 w-9 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur"
                title="Herunterladen"
                data-testid="chat-doc-preview-download"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={closeDocPreview}
                className="h-9 w-9 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur"
                data-testid="chat-doc-preview-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <iframe src={docPreview} title={att.filename} className="flex-1 w-full rounded-lg bg-white" />
        </div>
      )}
    </div>
  );
}

export function TeamChat({ conv = "team", dark = false, threadVisible = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [spOpen, setSpOpen] = useState(false);
  const [reactFor, setReactFor] = useState(null);
  const [forwardFor, setForwardFor] = useState(null);
  const reactionsSinceRef = useRef(new Date().toISOString());
  const listRef = useRef(null);
  const sendingRef = useRef(false);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  useEffect(() => {
    // Opening a conversation must not summon the iPhone keyboard and move
    // both navigation bars before the user has chosen to type.
    if (window.matchMedia("(max-width: 1023px)").matches) return undefined;
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [conv, threadVisible]);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const isNearBottom = () => {
    const el = listRef.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 250;
  };

  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [newBelow, setNewBelow] = useState(0);
  const stickRef = useRef(true);
  const onListScroll = () => {
    const near = isNearBottom();
    stickRef.current = near;
    setAwayFromBottom(!near);
    if (near) setNewBelow(0);
  };
  const jumpToLatest = () => {
    stickRef.current = true;
    setNewBelow(0);
    setAwayFromBottom(false);
    scrollDown();
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let raf = 0;
    const apply = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (stickRef.current) el.scrollTop = el.scrollHeight;
      });
    };
    const mo = new MutationObserver(apply);
    mo.observe(el, { childList: true, subtree: true });
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    const onMediaLoad = (e) => {
      if (e.target.tagName === "IMG" || e.target.tagName === "VIDEO") apply();
    };
    el.addEventListener("load", onMediaLoad, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      mo.disconnect();
      ro.disconnect();
      el.removeEventListener("load", onMediaLoad, true);
    };
  }, []);

  const markRead = useCallback(() => {
    api.post(`/chat/read`, { conv }).catch(() => {});
  }, [conv]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    Promise.all([
      api.get(`/chat/messages`, { params: { conv, limit: 200 } }),
      api.get(`/chat/members`),
    ])
      .then(([m, mem]) => {
        if (cancelled) return;
        setMessages(m.data);
        setMembers(mem.data);
        markRead();
        stickRef.current = true;
        [0, 150, 450, 1000].forEach((ms) => setTimeout(scrollDown, ms));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));

    const iv = setInterval(() => {
      const last = messagesRef.current[messagesRef.current.length - 1];
      api
        .get(`/chat/messages`, { params: { conv, ...(last ? { after: last.created_at } : {}) } })
        .then(({ data }) => {
          if (cancelled || !data.length) return;
          setMessages((prev) => {
            const ids = new Set(prev.map((x) => x.id));
            return [...prev, ...data.filter((x) => !ids.has(x.id))];
          });
          markRead();
          if (isNearBottom()) scrollDown();
          else setNewBelow((n) => n + data.length);
        })
        .catch(() => {});
      api
        .get(`/chat/reactions`, { params: { conv, since: reactionsSinceRef.current } })
        .then(({ data }) => {
          if (cancelled || !data.length) return;
          const map = new Map(data.map((r) => [r.id, r]));
          data.forEach((r) => {
            if (r.reactions_updated_at > reactionsSinceRef.current) reactionsSinceRef.current = r.reactions_updated_at;
          });
          setMessages((prev) => prev.map((x) => {
            const r = map.get(x.id);
            if (!r) return x;
            return { ...x, reactions: r.reactions, ...(r.transcript ? { transcript: r.transcript } : {}) };
          }));
        })
        .catch(() => {});
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [conv, markRead]);

  const addFiles = async (files) => {
    if (!files.length) return;
    const tooBig = files.filter((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig.length) toast.error(`Zu groß (max. ${MAX_MB} MB): ${tooBig.map((f) => f.name).join(", ")}`);
    const ok = files.filter((f) => f.size <= MAX_MB * 1024 * 1024);
    if (!ok.length) return;
    setUploading(true);
    let added = 0;
    for (let i = 0; i < ok.length; i++) {
      const file = ok[i];
      setUploadLabel(ok.length > 1 ? `Datei ${i + 1}/${ok.length}: ${file.name}` : file.name);
      setUploadPct(0);
      try {
        const data = await uploadFileChunked(file, { onProgress: setUploadPct });
        setAttachments((prev) => [...prev, { file_id: data.id, url: data.url, kind: data.kind, filename: file.name }]);
        added += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || `Upload fehlgeschlagen (${err.response?.status || "Netzwerkfehler"})`}`);
      }
    }
    setUploading(false);
    setUploadPct(0);
    setUploadLabel("");
    if (added > 0) toast.success(added === 1 ? "Datei angehängt — jetzt senden ➤" : `${added} Dateien angehängt — jetzt senden ➤`);
  };

  const onPickFile = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(files);
  };

  // Screenshot/Bild aus der Zwischenablage direkt einfügen (Strg+V / Cmd+V)
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter(Boolean)
      .map((f) => {
        if (!f.name || f.name === "image.png" || f.name === "image.jpg") {
          const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
          const ts = new Date().toLocaleString("de-DE").replace(/[.,: ]+/g, "-");
          return new File([f], `Screenshot-${ts}.${ext}`, { type: f.type });
        }
        return f;
      });
    if (!files.length) return;
    e.preventDefault();
    addFiles(files);
  };

  const newClientId = () =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // Sendet mit 1x Auto-Retry bei Netzwerk-/Serverfehlern (client_id verhindert Duplikate)
  const postMessage = async (payload) => {
    const body = { ...payload, client_id: newClientId() };
    try {
      const { data } = await api.post(`/chat/messages`, body);
      return data;
    } catch (err) {
      const st = err.response?.status;
      const retryable = !err.response || st >= 500 || st === 429;
      if (!retryable) throw err;
      await new Promise((r) => setTimeout(r, 1500));
      const { data } = await api.post(`/chat/messages`, body);
      return data;
    }
  };

  const send = async () => {
    const t = text.trim();
    if ((!t && attachments.length === 0) || sending || sendingRef.current) return;
    sendingRef.current = true;
    const mentions = members.filter((m) => t.includes(`@${m.name}`)).map((m) => m.id);
    setSending(true);
    try {
      const queue = attachments.length ? attachments : [null];
      for (let i = 0; i < queue.length; i++) {
        const payload = { conv, text: i === 0 ? t : "", attachment: queue[i], mentions: i === 0 ? mentions : [] };
        if (replyTo && i === 0) {
          payload.reply_to = {
            id: replyTo.id,
            user_name: replyTo.user_name,
            text: replyTo.text || (replyTo.attachment ? `📎 ${replyTo.attachment.filename || "Datei"}` : ""),
            kind: replyTo.attachment?.kind || "",
          };
        }
        const data = await postMessage(payload);
        setMessages((prev) => [...prev, data]);
      }
      setText("");
      setAttachments([]);
      setReplyTo(null);
      setEmojiOpen(false);
      setMentionOpen(false);
      scrollDown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Senden fehlgeschlagen (Verbindungsproblem) — dein Text bleibt erhalten, bitte nochmal auf Senden tippen");
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  const sendVoice = async (file, duration) => {
    setUploading(true);
    setUploadLabel("Sprachnachricht");
    setUploadPct(0);
    try {
      const data = await uploadFileChunked(file, { onProgress: setUploadPct });
      const payload = {
        conv, text: "", mentions: [],
        attachment: { file_id: data.id, url: data.url, kind: data.kind || "audio", filename: file.name, duration },
      };
      const { data: msg } = await api.post(`/chat/messages`, payload);
      setMessages((prev) => [...prev, msg]);
      scrollDown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sprachnachricht konnte nicht gesendet werden");
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadLabel("");
    }
  };

  const copyMessage = async (m) => {
    const txt = m.text || m.transcript || "";
    if (!txt) return toast.error("Nichts zum Kopieren");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Nachricht kopiert 📋");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Nachricht kopiert 📋");
      } catch {
        toast.error("Kopieren fehlgeschlagen");
      }
      ta.remove();
    }
  };

  const toggleReaction = async (m, emoji) => {
    setReactFor(null);
    try {
      const { data } = await api.post(`/chat/messages/${m.id}/react`, { emoji });
      setMessages((prev) => prev.map((x) => (x.id === data.id ? { ...x, reactions: data.reactions } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reaktion fehlgeschlagen");
    }
  };

  const lpTimer = useRef(null);
  const startLongPress = (id) => {
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => {
      setReactFor(id);
      if (navigator.vibrate) navigator.vibrate(12);
    }, 420);
  };
  const cancelLongPress = () => clearTimeout(lpTimer.current);

  const jumpToMessage = (id) => {
    const el = document.querySelector(`[data-testid="chat-message-${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("chat-jump-flash");
      setTimeout(() => el.classList.remove("chat-jump-flash"), 1200);
    }
  };

  const onChangeText = (v) => {
    setText(v);
    setMentionOpen(conv === "team" && /@[\wäöüÄÖÜß]*$/.test(v));
  };

  const mentionCandidates = mentionOpen
    ? members.filter((m) => {
        const q = (text.match(/@([\wäöüÄÖÜß]*)$/) || [])[1] || "";
        return m.id !== String(user?.id) && m.name.toLowerCase().startsWith(q.toLowerCase());
      })
    : [];

  const insertMention = (name) => {
    setText((t) => t.replace(/@[\wäöüÄÖÜß]*$/, `@${name} `));
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const memberNames = members.map((m) => m.name).filter(Boolean);

  let lastDay = null;
  const grouped = [];
  for (const msg of messages) {
    if ((msg.user_id || "").startsWith("system")) {
      const last = grouped[grouped.length - 1];
      if (last?.sys) last.items.push(msg);
      else grouped.push({ sys: true, items: [msg], id: `sys-${msg.id}` });
    } else {
      grouped.push({ sys: false, m: msg, id: msg.id });
    }
  }

  return (
    <div className={`flex flex-col h-full min-h-0 ${dark ? "text-white" : "text-neutral-900"}`} data-testid="team-chat">
      <SharePointPickerDialog
        open={spOpen}
        onClose={() => setSpOpen(false)}
        onPicked={(att) => {
          setAttachments((prev) => [...prev, att]);
          toast.success("SharePoint-Datei angehängt — jetzt senden ➤");
        }}
      />
      <ForwardDialog
        message={forwardFor}
        dark={dark}
        onClose={() => setForwardFor(null)}
        onForwarded={(targetConv, msg) => {
          if (targetConv === conv) {
            setMessages((prev) => [...prev, msg]);
            scrollDown();
          }
        }}
      />
      <div ref={listRef} onScroll={onListScroll} data-testid="chat-messages-list" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-2.5 sm:px-4 py-3 space-y-3 [overflow-anchor:none] [-webkit-overflow-scrolling:touch]">
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className={`h-5 w-5 animate-spin ${dark ? "text-neutral-500" : "text-neutral-400"}`} />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className={`text-center py-10 text-sm ${dark ? "text-neutral-500" : "text-neutral-400"}`} data-testid="chat-empty">
            Noch keine Nachrichten. Starte den Austausch mit deinem Team! 💬
          </div>
        )}
        {grouped.map((g) => {
          const first = g.sys ? g.items[0] : g.m;
          const day = dayLabel(first.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          if (g.sys) {
            return (
              <div key={g.id}>
                {showDay && (
                  <div className="flex justify-center my-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${dark ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-500"}`}>
                      {day}
                    </span>
                  </div>
                )}
                <SystemActivityGroup items={g.items} dark={dark} onOpenRef={(cref) => navigate(`/pipeline?focus=${cref.id}`)} />
              </div>
            );
          }
          const m = g.m;
          const mine = m.user_id === String(user?.id);
          return (
            <div key={m.id}>
              {showDay && (
                <div className="flex justify-center my-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${dark ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-500"}`}>
                    {day}
                  </span>
                </div>
              )}
              <div className={`group flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.id}`}>
                <div className="mt-0.5">
                  <UserAvatar src={m.avatar_url} name={m.user_name} color={m.avatar_color} className="h-7 w-7" textClass="text-[10px]" />
                </div>
                <div className={`min-w-0 max-w-[calc(100%-5rem)] lg:max-w-[calc(100%-11rem)] ${mine ? "items-end" : ""}`}>
                  <div className={`flex items-baseline gap-2 mb-0.5 ${mine ? "flex-row-reverse" : ""}`}>
                    <span className={`text-xs font-semibold ${dark ? "text-neutral-300" : "text-neutral-700"}`}>{mine ? "Du" : m.user_name}</span>
                    <span className={`text-[10px] ${dark ? "text-neutral-500" : "text-neutral-400"}`}>
                      {new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div
                    data-testid={`chat-bubble-${m.id}`}
                    onTouchStart={() => startLongPress(m.id)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onContextMenu={(e) => {
                      const sel = window.getSelection?.();
                      if (sel && !sel.isCollapsed) return;
                      e.preventDefault();
                      setReactFor(m.id);
                    }}
                    style={{ WebkitTouchCallout: "none" }}
                    className={`select-none sm:select-text rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      mine
                        ? "bg-[#007AFF] text-white rounded-tr-sm"
                        : dark
                          ? "bg-[#2C2C2E] text-neutral-100 rounded-tl-sm"
                          : "bg-zinc-200/80 text-zinc-900 rounded-tl-sm"
                    }`}
                  >
                    {m.forwarded_from && (
                      <div className={`flex items-center gap-1 text-[10px] italic mb-0.5 ${mine ? "text-white/70" : "opacity-60"}`} data-testid="chat-forwarded-label">
                        <Forward className="h-3 w-3" /> Weitergeleitet{m.forwarded_from.user_name ? ` von ${m.forwarded_from.user_name}` : ""}
                      </div>
                    )}
                    {m.reply_to && (
                      <ReplyQuote r={m.reply_to} mine={mine} dark={dark} onJump={() => jumpToMessage(m.reply_to.id)} />
                    )}
                    {renderText(m.text, memberNames, mine ? false : dark)}
                    <ContentRefCard
                      cref={m.content_ref}
                      dark={dark}
                      onOpen={() => navigate(`/pipeline?focus=${m.content_ref.id}`)}
                    />
                    <Attachment att={m.attachment} dark={dark} transcript={m.transcript} />
                  </div>
                  <ReactionChips m={m} uid={String(user?.id)} dark={dark} onToggle={toggleReaction} />
                </div>
                <div className="self-center hidden lg:flex items-center gap-0.5 shrink-0">
                  <button
                    data-testid={`chat-react-btn-${m.id}`}
                    onClick={() => setReactFor((r) => (r === m.id ? null : m.id))}
                    title="Reagieren"
                    className={`h-7 w-7 rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${
                      dark ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-400 hover:bg-neutral-100"
                    }`}
                  >
                    <SmilePlus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`chat-reply-btn-${m.id}`}
                    onClick={() => {
                      setReplyTo(m);
                      inputRef.current?.focus();
                    }}
                    title="Antworten"
                    className={`h-7 w-7 rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${
                      dark ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-400 hover:bg-neutral-100"
                    }`}
                  >
                    <Reply className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`chat-forward-btn-${m.id}`}
                    onClick={() => setForwardFor(m)}
                    title="Weiterleiten"
                    className={`h-7 w-7 rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${
                      dark ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-400 hover:bg-neutral-100"
                    }`}
                  >
                    <Forward className="h-3.5 w-3.5" />
                  </button>
                  {(m.text || m.transcript) && (
                    <button
                      data-testid={`chat-copy-btn-${m.id}`}
                      onClick={() => copyMessage(m)}
                      title="Text kopieren"
                      className={`h-7 w-7 rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${
                        dark ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-400 hover:bg-neutral-100"
                      }`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`chat-actions-btn-${m.id}`}
                  onClick={() => setReactFor(m.id)}
                  aria-label={`Aktionen für Nachricht von ${mine ? "dir" : m.user_name}`}
                  className={`lg:hidden self-center h-8 w-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                    dark ? "text-neutral-400 active:bg-neutral-800" : "text-neutral-500 active:bg-neutral-200"
                  }`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {reactFor && (() => {
        const target = messages.find((x) => x.id === reactFor);
        if (!target) return null;
        return (
          <div className="fixed inset-0 z-[10012] flex items-end sm:items-center justify-center" data-testid="chat-react-sheet">
            <div className="absolute inset-0 bg-black/30" data-testid="chat-react-backdrop" onClick={() => setReactFor(null)} />
            <div
              data-testid="chat-react-palette"
              className={`relative w-full sm:w-auto sm:min-w-[340px] mx-3 mb-6 sm:mb-0 rounded-2xl shadow-2xl border p-3 ${
                dark ? "bg-neutral-800 border-neutral-700" : "bg-white border-neutral-200"
              }`}
            >
              <p className={`text-[11px] font-semibold truncate mb-2 px-1 ${dark ? "text-neutral-400" : "text-neutral-400"}`}>
                {target.user_id === String(user?.id) ? "Du" : target.user_name}: {(target.text || "📎 Anhang").slice(0, 60)}
              </p>
              <div className="flex items-center justify-around">
                {QUICK_REACTIONS.map((e) => (
                  <button
                    key={e}
                    data-testid={`chat-react-pick-${target.id}-${e}`}
                    onClick={() => toggleReaction(target, e)}
                    className="h-11 w-11 rounded-full text-[24px] hover:scale-125 active:scale-90 transition-transform flex items-center justify-center"
                  >
                    {e}
                  </button>
                ))}
              </div>
              <div className="mt-2.5 grid grid-cols-3 gap-2">
                <button
                  data-testid="chat-react-reply"
                  onClick={() => { setReplyTo(target); setReactFor(null); inputRef.current?.focus(); }}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-[11px] font-semibold transition ${
                    dark ? "bg-neutral-700 text-neutral-100 hover:bg-neutral-600" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  <Reply className="h-4 w-4" /> Antworten
                </button>
                <button
                  data-testid="chat-react-forward"
                  onClick={() => { setForwardFor(target); setReactFor(null); }}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-[11px] font-semibold transition ${
                    dark ? "bg-neutral-700 text-neutral-100 hover:bg-neutral-600" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  <Forward className="h-4 w-4" /> Weiterleiten
                </button>
                <button
                  data-testid="chat-react-copy"
                  onClick={() => { copyMessage(target); setReactFor(null); }}
                  disabled={!target?.text && !target?.transcript}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-[11px] font-semibold transition disabled:opacity-40 ${
                    dark ? "bg-neutral-700 text-neutral-100 hover:bg-neutral-600" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  <Copy className="h-4 w-4" /> Kopieren
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div data-testid="chat-composer" className={`relative shrink-0 border-t px-3 py-2.5 backdrop-blur-xl ${dark ? "border-zinc-800/60 bg-black/60" : "border-zinc-200/60 bg-white/75"}`}>
        {awayFromBottom && (
          <button
            data-testid="chat-jump-latest"
            onClick={jumpToLatest}
            className={`absolute -top-14 right-4 z-10 flex items-center gap-1.5 pl-3 pr-3.5 py-2 rounded-full shadow-lg text-xs font-bold transition ${
              dark ? "bg-neutral-100 text-neutral-900 hover:bg-white" : "bg-neutral-900 text-white hover:bg-neutral-700"
            }`}
          >
            <ChevronDown className="h-4 w-4" />
            <span className="hidden sm:inline">{newBelow > 0 ? `${newBelow} neue Nachricht${newBelow === 1 ? "" : "en"}` : "Zur letzten Nachricht"}</span>
            {newBelow > 0 && <span className="sm:hidden">{newBelow}</span>}
          </button>
        )}
        {mentionCandidates.length > 0 && (
          <div className={`absolute bottom-full left-3 mb-1 w-60 rounded-xl border shadow-xl overflow-hidden z-10 ${dark ? "bg-neutral-800 border-neutral-700" : "bg-white border-neutral-200"}`}>
            {mentionCandidates.slice(0, 5).map((m) => (
              <button
                key={m.id}
                data-testid={`chat-mention-${m.id}`}
                onClick={() => insertMention(m.name)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm ${dark ? "hover:bg-neutral-700 text-neutral-100" : "hover:bg-neutral-50 text-neutral-800"}`}
              >
                <UserAvatar src={m.avatar_url} name={m.name} color={m.avatar_color} className="h-6 w-6" textClass="text-[9px]" />
                <span className="font-medium">{m.name}</span>
                <span className={`text-[10px] ml-auto ${dark ? "text-neutral-400" : "text-neutral-400"}`}>{m.role === "admin" ? "Redaktion" : "Creator"}</span>
              </button>
            ))}
          </div>
        )}
        {uploading && (
          <div className={`flex items-center gap-2 mb-2 rounded-lg px-2.5 py-1.5 text-xs ${dark ? "bg-neutral-800 text-neutral-200" : "bg-neutral-100 text-neutral-700"}`} data-testid="chat-upload-progress">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span className="shrink-0 font-semibold truncate max-w-[55%]">{uploadPct < 100 ? `${uploadLabel || "Lädt hoch…"} ${uploadPct}%` : "Wird gespeichert…"}</span>
            <span className="flex-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
              <span className="block h-full bg-blue-600 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
            </span>
          </div>
        )}
        {attachments.map((att, ai) => (
          <div key={att.file_id || ai} data-testid="chat-composer-attachment" className={`flex items-center gap-2.5 mb-2 rounded-xl px-2.5 py-2 text-xs ${dark ? "bg-neutral-800 text-neutral-200" : "bg-neutral-100 text-neutral-700"}`}>
            {att.kind === "image" ? (
              <img src={resolveMediaUrl(att.url)} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0 border border-black/10" />
            ) : att.kind === "video" ? (
              <video src={resolveMediaUrl(att.url)} muted className="h-14 w-14 rounded-lg object-cover shrink-0 border border-black/10 bg-black" />
            ) : (
              <FileText className="h-5 w-5 shrink-0 opacity-70" />
            )}
            <div className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{att.filename}</span>
              <span className="block text-[10px] opacity-60">
                {attachments.length > 1 ? `Anhang ${ai + 1}/${attachments.length} — ` : "Angehängt — "}zum Senden auf ➤ tippen
              </span>
            </div>
            <button data-testid="chat-remove-attachment" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== ai))} className="opacity-60 hover:opacity-100 shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {replyTo && (
          <div
            data-testid="chat-reply-preview"
            className={`flex items-center gap-2.5 mb-2 rounded-xl px-2.5 py-2 text-xs border-l-4 ${
              dark ? "bg-neutral-800 text-neutral-200 border-blue-400" : "bg-blue-50 text-neutral-700 border-blue-500"
            }`}
          >
            <Reply className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <div className="min-w-0 flex-1">
              <span className={`block font-bold ${dark ? "text-blue-300" : "text-blue-700"}`}>
                {replyTo.user_id === String(user?.id) ? "Du" : replyTo.user_name}
              </span>
              <span className="block truncate opacity-75">
                {replyTo.text || (replyTo.attachment ? `📎 ${replyTo.attachment.filename || "Datei"}` : "")}
              </span>
            </div>
            <button data-testid="chat-reply-cancel" onClick={() => setReplyTo(null)} className="opacity-60 hover:opacity-100 shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {emojiOpen && (
          <div className="absolute bottom-full right-2 mb-2 z-20 rounded-2xl overflow-hidden shadow-2xl" data-testid="chat-emoji-picker">
            <Suspense
              fallback={
                <div className={`w-[300px] h-[340px] flex items-center justify-center ${dark ? "bg-neutral-800" : "bg-white"}`}>
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(e) => setText((t) => t + e.emoji)}
                theme={dark ? "dark" : "light"}
                width={300}
                height={340}
                searchPlaceHolder="Emoji suchen…"
                previewConfig={{ showPreview: false }}
                lazyLoadEmojis
                skinTonesDisabled
              />
            </Suspense>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple className="hidden" onChange={onPickFile} accept="image/*,video/*,.pdf,.docx,.xlsx,.pptx,.txt,.csv,.zip" />
          <button
            data-testid="chat-attach-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Datei anhängen"
            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dark ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"}`}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <button
            data-testid="chat-sharepoint-btn"
            onClick={() => setSpOpen(true)}
            disabled={uploading}
            title="Aus SharePoint anhängen"
            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dark ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"}`}
          >
            <Cloud className="h-4 w-4" />
          </button>
          <button
            data-testid="chat-emoji-btn"
            onClick={() => setEmojiOpen((o) => !o)}
            title="Emoji einfügen"
            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
              emojiOpen
                ? "bg-blue-600 text-white"
                : dark
                  ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
            }`}
          >
            <Smile className="h-4 w-4" />
          </button>
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            value={text}
            onChange={(e) => onChangeText(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={conv === "team" && window.innerWidth >= 640 ? "Nachricht… (@Name erwähnt jemanden)" : "Nachricht…"}
            className={`min-w-0 flex-1 resize-none rounded-xl px-3.5 py-2.5 text-base sm:text-sm max-h-28 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
              dark ? "bg-neutral-800 border border-neutral-700 text-white placeholder:text-neutral-500" : "bg-neutral-50 border border-neutral-200 text-neutral-900"
            }`}
          />
          {!text.trim() && attachments.length === 0 ? (
            <VoiceRecorderButton dark={dark} disabled={uploading || sending} onRecorded={sendVoice} />
          ) : (
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={sending || uploading}
              className="h-10 w-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white flex items-center justify-center shrink-0"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
